import { useCallback } from 'react';
import { web2app } from '@kcd/app-interface';
import { Button, Checkbox, useToast } from '@kcd/design-system';

import {
  useDeleteShoppingCartItems,
  useToggleVendorGroupAllItemsSelect,
} from '~/domains/cart/queries';
import { useOAIInvalidateQueries } from '~/hooks/reactQuery';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { OAISchema } from '~/types/openapi';
import { changedByFontScale } from '~/utils/fontScale';

type Props = {
  totalCount: number;
  checkedCount: number;
  allChecked: boolean;
  flatList: OAISchema['MarketShoppingCartItemResponseV2'][];
};

export const CartCheckAllBox = ({
  totalCount,
  checkedCount,
  allChecked,
  flatList,
}: Props) => {
  const toggleMutate = useToggleVendorGroupAllItemsSelect();
  const deleteMutate = useDeleteShoppingCartItems();
  const invalidateQueries = useOAIInvalidateQueries();
  const toast = useToast();

  const toggleAllSelect = useCallback(() => {
    toggleMutate(
      {
        requestBody: {
          isSelected: !allChecked,
          cartType: 'SHOPPING_CART',
        },
      },
      {
        onError: (err) => {
          if (err instanceof OAIApiError) {
            const errMessage = getOAIErrorMessage(err);
            toast(errMessage, { offset: 100 });
          }
        },
      },
    );
  }, [allChecked, toast, toggleMutate]);

  const onDeleteCheckedItem = useCallback(async () => {
    const checkedIdList = flatList
      .filter(({ isSelected }) => isSelected)
      .map(({ shoppingCartItemId }) => shoppingCartItemId) as number[];

    try {
      const { confirmed } = await web2app.openDialogAsync({
        type: 'destructive',
        description: '상품을 삭제하시겠습니까?',
      });

      if (!confirmed) {
        return;
      }
      await deleteMutate({
        requestBody: {
          shoppingCartItemIds: checkedIdList,
        },
      });
      void invalidateQueries(
        '/api/market-place/v3/shopping-cart-items',
        '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
      );
      toast('상품이 삭제되었습니다', { offset: 100 });
    } catch (err) {
      if (err instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(err);
        toast(errMessage);
        return;
      }
      throw err;
    }
  }, [flatList, deleteMutate, invalidateQueries, toast]);

  return (
    <section className="flex h-[54px] items-center justify-between px-20">
      <Checkbox checked={allChecked} onChange={toggleAllSelect}>
        전체선택 ({checkedCount}/{totalCount})
      </Checkbox>
      {checkedCount ? (
        <Button variant="outline" size="xs" onClick={onDeleteCheckedItem}>
          선택{changedByFontScale() && <br />}삭제
        </Button>
      ) : (
        <></>
      )}
    </section>
  );
};
